"use client";
import { Box } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";

/**
 * FreeTrialBadge 显示“7-day Free”角标，带周期性整体放大和逐字下落动画。
 * 整个组件（外层弹出 + 逐字掉落）以 20s 为一个周期重复播放：
 *  - 第一个 ~3% 时间段完成弹出（约 0.6s）
 *  - 文本字符依次掉落（总约 < 1.5s）
 *  - 余下时间保持静止直至下一个 20s 周期
 */
export default function FreeTrialBadge() {
  const t = useTranslations("TrialBadge");
  const text = t("free");
  const isCJK = useMemo(() => /[\u4e00-\u9fff]/.test(text), [text]);
  const [cycle, setCycle] = useState(0);
  // 20s 周期
  const CYCLE_MS = 20000;
  // 盒子弹出阶段占比（百分比 * 20s）这里使用 3% ≈ 600ms
  const POP_PERCENT = 0.03;
  const POP_MS = CYCLE_MS * POP_PERCENT; // ≈600ms
  // 为了让落字更连贯，字符动画可以在弹出完成前稍微“预热”开始。
  // 提前时间（重叠量）：CJK 略大一些以强调动势。
  const OVERLAP_MS = isCJK ? 160 : 110; // 必须 < POP_MS
  const BASE_CHAR_DURATION = isCJK ? 420 : 300; // ms
  const BASE_CHAR_STAGGER = isCJK ? 45 : 50; // ms

  useEffect(() => {
    const id = setInterval(() => setCycle((c) => c + 1), CYCLE_MS);
    return () => clearInterval(id);
  }, [CYCLE_MS]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: { xs: "-0.8rem", lg: "-0.6rem" },
        right: { xs: "-0.6rem", lg: "-0.6rem" },
        background: "linear-gradient(270deg, #9832FF -25.93%, #28FF84 116.05%)",
        border: "1px solid rgba(255,255,255,0.5)",
        color: "#fff",
        fontSize: { xs: "0.6rem", lg: "0.6875rem" },
        fontWeight: 600,
        lineHeight: 1.6,
        px: "0.37rem",
        borderRadius: "999px 999px 999px 0", // 左下角直角
        boxShadow: 1,
        letterSpacing: 0.5,
        textWrap: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transformOrigin: "left bottom",
        // 外层弹出动画：20s 一个周期；前 3% (≈0.6s) 完成放大；字符在弹出完成后再开始掉落
        animation: `badgePop ${CYCLE_MS}ms cubic-bezier(.55,1.4,.55,.95) infinite`,
        "@keyframes badgePop": {
          "0%": { opacity: 0, transform: "scale(0)" },
          "3%": { opacity: 1, transform: "scale(1)" },
          "100%": { opacity: 1, transform: "scale(1)" },
        },
        "@keyframes charDrop": {
          "0%": { opacity: 0, transform: "translateY(-12px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}>
      {[...text].map((ch, i) => {
        const isSpace = ch === " ";
        // 让字符在弹出结束前 OVERLAP_MS 即开始（首字符），造成更顺滑的承接。
        let delayMs = POP_MS - OVERLAP_MS + i * BASE_CHAR_STAGGER;
        if (delayMs < 0) delayMs = 0;
        return (
          <Box
            key={`${cycle}-${i}`}
            component="span"
            sx={{
              display: "inline-block",
              opacity: 0,
              transform: "translateY(-12px)",
              animation: `charDrop ${BASE_CHAR_DURATION}ms ease-out forwards`,
              animationDelay: `${delayMs}ms`,
              ...(isSpace ? { width: "0.3rem" } : {}),
              willChange: "transform, opacity",
            }}>
            {ch}
          </Box>
        );
      })}
    </Box>
  );
}
